import type { Node, NodeProps } from '@xyflow/react';
import React, { useMemo } from 'react';
import { cn } from '../../../helpers/util';
import { useAppSelector } from '../../../redux/hooks';
import { selectCausesLength, selectControlsLengthByCauseId, selectMoving } from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';
import { type HandlesProps } from './common/handles.component';
import VerticalMoveControl, { VERTICAL_STEP, VerticalMoveDirection } from './common/vertical-move-control.component';

type CauseNodeData = BaseNodeData;
export type CauseNodeType = Node<CauseNodeData>;
type CauseNodeProps = NodeProps<CauseNodeType>;

const className = 'bg-causes-box-bg border-causes-box-border text-causes-box-fg';
const iconClassName = 'text-causes-box-bg stroke-causes-box-fg';
const handles: HandlesProps = { rightHandleId: 'right', rightHandleType: 'source' };
const staticData = { className, iconClassName, handles };

/**
 * A component that represents a cause node in a flow diagram.
 * Extends the base node component with cause-specific functionality.
 *
 * @component
 * @param {CauseNodeProps} props - The properties for the cause node component
 * @returns {JSX.Element} A rendered cause node component
 *
 * @example
 * ```tsx
 * <CauseNode
 *   id="cause-1"
 *   data={{ label: "Root Cause" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const CauseNode = (props: CauseNodeProps): React.JSX.Element => {
  const causesLength = useAppSelector(selectCausesLength);
  const controlsLength = useAppSelector((state) => selectControlsLengthByCauseId(state, props.data.recordId!));

  const moving = useAppSelector(selectMoving);
  const { rowIndex } = props.data;

  const canMoveUp = useMemo(() => (rowIndex !== undefined ? rowIndex >= VERTICAL_STEP : false), [rowIndex]);

  const canMoveDown = useMemo(
    () => (rowIndex !== undefined ? rowIndex < causesLength - VERTICAL_STEP : false),
    [rowIndex, causesLength]
  );

  const data = useMemo(() => {
    const className = cn(staticData.className, { 'animate-wobble': moving && (canMoveUp || canMoveDown) });
    // a cause can be unlinked even when it has controls — orphaned controls are cascade-unlinked (MYOSH-14080)
    return {
      ...props.data,
      ...staticData,
      className,
      showUnlinkActionIcon: true,
      linkedControlsCount: controlsLength,
    };
  }, [props.data, canMoveUp, canMoveDown, moving, controlsLength]);

  return (
    <div className="group flex items-center">
      <VerticalMoveControl
        id={props.id}
        type="cause-node"
        index={data.rowIndex!}
        title="Move Up"
        direction={VerticalMoveDirection.UP}
        disabled={!moving || !canMoveUp}
      />

      <BaseNode {...props} data={data} />

      <VerticalMoveControl
        id={props.id}
        type="cause-node"
        index={data.rowIndex!}
        title="Move Down"
        direction={VerticalMoveDirection.DOWN}
        disabled={!moving || !canMoveDown}
      />
    </div>
  );
};

export default CauseNode;
